import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  setDoc,
  deleteDoc,
  getDoc,
  DocumentData,
  updateDoc,
  collectionData,
} from '@angular/fire/firestore';
import { Observable, from, firstValueFrom, Subscription, catchError, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { ShopProduct } from '../interfaces/shop-product.interface';
import { StorageService } from './storage.service';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface StripeProductResponse {
  productId: string;
  priceIds: {
    fullPayment: string;
    sixMonths: string;
    twelveMonths: string;
    eighteenMonths: string;
    thirtyMonths: string;
  };
}

interface StripePriceResponse {
  productId: string;
  priceIds: {
    fullPayment: string;
    sixMonths: string;
    twelveMonths: string;
    eighteenMonths: string;
    thirtyMonths: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ShopService {
  private readonly collectionName = 'shop-products';
  private readonly storagePath = 'products';
  private apiUrl = environment.apiUrl;
  private headers = new HttpHeaders()
    .set('Content-Type', 'application/json')
    .set('Accept', 'application/json');

  constructor(
    private firestore: Firestore,
    private storageService: StorageService,
    private http: HttpClient,
  ) {}

  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Ein unerwarteter Fehler ist aufgetreten.';

    if (error.status === 0) {
      errorMessage = 'Verbindung zum Server fehlgeschlagen. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut.';
    } else if (error.error instanceof ErrorEvent) {
      errorMessage = `Client-Fehler: ${error.error.message}`;
    } else {
      errorMessage = `Server-Fehler: ${error.status} ${error.statusText}`;
      if (error.error?.message) {
        errorMessage += ` - ${error.error.message}`;
      }
    }

    return throwError(() => new Error(errorMessage));
  }

  getAllProducts(): Observable<ShopProduct[]> {
    const productsCollection = collection(this.firestore, this.collectionName);
    return collectionData(productsCollection, { idField: 'id' }).pipe(
      map(products => products as ShopProduct[])
    );
  }

  getProduct(id: string): Observable<ShopProduct | undefined> {
    const docRef = doc(this.firestore, this.collectionName, id);
    return from(getDoc(docRef)).pipe(
      map(doc => doc.exists() ? { id: doc.id, ...doc.data() } as ShopProduct : undefined)
    );
  }

  private ensureTaxMetadata(metadata?: any) {
    return {
      tax_behavior: 'exclusive',
      tax_code: 'txcd_10000000',
      product_type: 'digital_goods',
      eu_vat: 'true',
      created_at: new Date().toISOString(),
      ...(metadata || {})
    };
  }

  async createProduct(product: Partial<ShopProduct>, imageFile?: File): Promise<void> {
    if (!product.price) {
      throw new Error('Product price is required');
    }

    const productsCollection = collection(this.firestore, this.collectionName);
    const docRef = doc(productsCollection);
    const imageUrl = imageFile ? await this.uploadProductImage(docRef.id, imageFile) : '';

    const response = await firstValueFrom(this.http.post<StripeProductResponse>(
      `${this.apiUrl}/stripe/products`,
      {
        name: product.name,
        description: product.description,
        price: product.price,
        tax_code: 'txcd_10000000',
        type: 'service',
        metadata: this.ensureTaxMetadata({
          ...(product.metadata || {}),
          courseIds: product.courseIds?.join(','),
          type: 'education'
        }),
        prices: this.createPriceConfigurations(product.price)
      }
    ));

    const productData: DocumentData = this.createProductData(product, imageUrl, response);
    await setDoc(docRef, productData);
  }

  private async uploadProductImage(docId: string, imageFile: File): Promise<string> {
    const imagePath = `${this.storagePath}/${docId}/${imageFile.name}`;
    const uploadResult = await new Promise<string>((resolve, reject) => {
      const subscription: Subscription = this.storageService.uploadProductImage(imageFile, imagePath).subscribe({
        next: (progress) => {
          if (progress.url) {
            resolve(progress.url);
            subscription.unsubscribe();
          }
        },
        error: (error) => {
          reject(error);
          subscription.unsubscribe();
        },
        complete: () => subscription.unsubscribe()
      });
    });
    return uploadResult;
  }

  private createPriceConfigurations(price: number) {
    return {
      fullPayment: {
        type: 'one_time',
        currency: 'eur',
        unit_amount: price * 100,
        tax_behavior: 'exclusive',
        tax_code: 'txcd_10000000',
        metadata: {
          tax_code: 'txcd_10000000',
          product_type: 'digital_goods'
        }
      },
      sixMonths: this.createInstallmentPrice(price, 6),
      twelveMonths: this.createInstallmentPrice(price, 12),
      eighteenMonths: this.createInstallmentPrice(price, 18),
      twentyFourMonths: this.createInstallmentPrice(price, 24),
      thirtyMonths: this.createInstallmentPrice(price, 30)
    };
  }

  private createInstallmentPrice(price: number, months: number) {
    return {
      type: 'recurring',
      currency: 'eur',
      unit_amount: Math.round((price * 100) / months),
      tax_behavior: 'exclusive',
      tax_code: 'txcd_10000000',
      product_type: 'digital_goods',
      metadata: {
        tax_code: 'txcd_10000000',
        product_type: 'digital_goods'
      },
      recurring: {
        interval: 'month',
        interval_count: 1,
        usage_type: 'licensed'
      }
    };
  }

  private createProductData(product: Partial<ShopProduct>, imageUrl: string, stripeResponse: StripeProductResponse): DocumentData {
    const productData: DocumentData = {};

    if (product.name) productData['name'] = product.name;
    if (typeof product.price === 'number') productData['price'] = product.price;
    if (product.description) productData['description'] = product.description;
    if (Array.isArray(product.courseIds)) productData['courseIds'] = product.courseIds;
    if (imageUrl) productData['image'] = imageUrl;
    if (typeof product.oldPrice === 'number') productData['oldPrice'] = product.oldPrice;
    if (product.tag) productData['tag'] = product.tag;

    productData['stripeProductId'] = stripeResponse.productId;
    productData['stripePriceIds'] = stripeResponse.priceIds;
    productData['tax_behavior'] = 'exclusive';
    productData['tax_code'] = 'txcd_10000000';
    productData['product_type'] = 'digital_goods';
    productData['metadata'] = this.ensureTaxMetadata({
      ...(product.metadata || {}),
      courseIds: product.courseIds?.join(','),
      type: 'education'
    });

    return productData;
  }

  async updateProduct(id: string, product: Partial<ShopProduct>, imageFile?: File): Promise<void> {
    const docRef = doc(this.firestore, this.collectionName, id);
    const existingProduct = await getDoc(docRef);
    const existingData = existingProduct.data() as ShopProduct;

    if (!existingData) {
      throw new Error('Product not found');
    }

    const updatedPrice = product.price ?? existingData.price;
    if (typeof updatedPrice !== 'number') {
      throw new Error('Valid price is required');
    }

    let imageUrl = product.image || '';
    if (imageFile) {
      imageUrl = await this.uploadProductImage(id, imageFile);
    }

    if (typeof product.price === 'number' && existingData.price !== product.price && existingData.stripeProductId) {
      const priceData = await this.updateStripePrices(existingData, product);
      product.stripePriceIds = priceData.priceIds;
    }

    const updateData = this.createUpdateData(product, imageUrl);
    await updateDoc(docRef, updateData);
  }

  private async updateStripePrices(existingData: ShopProduct, product: Partial<ShopProduct>) {
    // First create new prices
    const newPriceData = await firstValueFrom(this.http.post<StripePriceResponse>(
      `${this.apiUrl}/stripe/products/${existingData.stripeProductId}/prices`,
      {
        price: product.price,
        name: product.name || existingData.name,
        description: product.description || existingData.description,
        existingPriceIds: existingData.stripePriceIds,
        activate: true,
        forceActivate: true,
        prices: this.createPriceConfigurations(product.price!)
      }
    ));

    // Then deactivate old prices, passing the new default price ID
    if (existingData.stripePriceIds) {
      await firstValueFrom(this.http.post(
        `${this.apiUrl}/stripe/products/${existingData.stripeProductId}/deactivate-prices`,
        { 
          priceIds: Object.values(existingData.stripePriceIds),
          newDefaultPriceId: newPriceData.priceIds.fullPayment // Use the new full payment price as default
        }
      ));
    }

    return newPriceData;
  }

  private createUpdateData(product: Partial<ShopProduct>, imageUrl: string): DocumentData {
    const updateData: DocumentData = {};

    if (product.name) updateData['name'] = product.name;
    if (typeof product.price === 'number') updateData['price'] = product.price;
    if (product.description) updateData['description'] = product.description;
    if (Array.isArray(product.courseIds)) updateData['courseIds'] = product.courseIds;
    if (imageUrl) updateData['image'] = imageUrl;
    if (typeof product.oldPrice === 'number') updateData['oldPrice'] = product.oldPrice;
    if (product.tag) updateData['tag'] = product.tag;
    if (product.stripeProductId) updateData['stripeProductId'] = product.stripeProductId;
    if (product.stripePriceIds && 
        product.stripePriceIds.fullPayment && 
        product.stripePriceIds.sixMonths && 
        product.stripePriceIds.twelveMonths && 
        product.stripePriceIds.eighteenMonths && 
        product.stripePriceIds.thirtyMonths) {
      updateData['stripePriceIds'] = product.stripePriceIds;
    }

    return updateData;
  }

  async deleteProduct(id: string): Promise<void> {
    const docRef = doc(this.firestore, this.collectionName, id);
    await deleteDoc(docRef);
  }

  getStripeProduct(productId: string): Promise<any> {
    return this.http.get(`${this.apiUrl}/stripe/products/${productId}`).toPromise();
  }

  createStripeProduct(name: string, price: number): Promise<any> {
    return this.http.post(`${this.apiUrl}/stripe/products`, {
      name,
      price
    }).toPromise();
  }

  async ensureStripePrices(productId: string, basePrice: number, existingPriceIds?: {
    fullPayment?: string;
    sixMonths?: string;
    twelveMonths?: string;
    eighteenMonths?: string;
    thirtyMonths?: string;
  }): Promise<{
    fullPayment: string;
    sixMonths: string;
    twelveMonths: string;
    eighteenMonths: string;
    thirtyMonths: string;
  }> {
    const response = await this.http.post<{
      fullPayment: string;
      sixMonths: string;
      twelveMonths: string;
      eighteenMonths: string;
      thirtyMonths: string;
    }>(`${this.apiUrl}/stripe/products/${productId}/prices`, {
      basePrice,
      existingPriceIds
    }).toPromise();

    if (!response) {
      throw new Error('Failed to create/update Stripe prices');
    }

    return response;
  }

  updateProductPrices(productId: string, price: number, options: any = {}): Observable<StripePriceResponse> {
    return this.http.post<StripePriceResponse>(
      `${this.apiUrl}/stripe/products/${productId}/prices`,
      { price, ...options },
      { headers: this.headers }
    ).pipe(
      map(response => {
        const requiredPrices = ['fullPayment', 'sixMonths', 'twelveMonths', 'eighteenMonths', 'thirtyMonths'] as const;
        const missingPrices = requiredPrices.filter(key => !response.priceIds?.[key]);
        
        if (missingPrices.length > 0) {
          throw new Error(`Missing required price IDs: ${missingPrices.join(', ')}`);
        }

        return response;
      }),
      catchError(error => throwError(() => new Error(`Fehler beim Aktualisieren der Stripe-Preise: ${error.message || error}`)))
    );
  }

  deactivatePrices(productId: string, priceIds: string[]): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/stripe/products/${productId}/deactivate-prices`,
      { priceIds },
      { headers: this.headers }
    ).pipe(
      catchError(this.handleError)
    );
  }

  activatePrices(productId: string, priceIds: string[]): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/stripe/products/${productId}/activate-prices`,
      { priceIds },
      { headers: this.headers }
    ).pipe(
      catchError(this.handleError)
    );
  }
}